/* ==========================================================================
   PWR ANIMATION
   Scroll reveals, lazy media, parallax, count-up meters, and button ripple.
   Loaded AFTER pwr-utils, pwr-events, pwr-core. Registers window.PWR.animation.

   Design pairing:
     Works with pwr-animations.css. The CSS defines resting/visible states for
     [data-pwr-reveal] and [data-pwr-reveal-group]; this JS toggles .is-visible
     via a SHARED IntersectionObserver (one observer for the whole page, not
     one-per-element). All effects short-circuit under prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  window.PWR = window.PWR || {};
  if (window.PWR.animation) return;

  const { utils, bus, EVENTS } = window.PWR;

  const reduced = () => utils.prefersReducedMotion();

  /* ==========================================================================
     SHARED REVEAL OBSERVER
     ========================================================================== */
  class RevealController {
    constructor() {
      this.observer = null;
      this._boot();
      // Re-scan on Theme Editor section loads.
      bus.on(EVENTS.sectionLoad, (d) => this.observe(d.target || document));
    }

    _boot() {
      // Arm the CSS hidden-state ONLY now that the reveal controller has
      // booted successfully. pwr-animations.css hides [data-pwr-reveal] only
      // under `.pwr-js`, so if any earlier script in the chain throws and this
      // never runs, content stays fully visible instead of a blank page.
      document.documentElement.classList.add('pwr-js');

      if (reduced() || !('IntersectionObserver' in window)) {
        // No motion: make everything immediately visible, no observer.
        utils.ready(() => this.revealAllInstantly(document));
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => this._onIntersect(entries),
        { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      utils.ready(() => this.observe(document));

      // If a user turns on reduced motion mid-session, reveal remaining nodes.
      bus.on(EVENTS.reducedMotionChange, (m) => {
        if (m.reducedMotion) this.revealAllInstantly(document);
      });
    }

    _targets(scope) {
      return utils.qsa('[data-pwr-reveal], [data-pwr-reveal-group]', scope || document);
    }

    observe(scope) {
      if (!this.observer) {
        this.revealAllInstantly(scope);
        return;
      }
      this._targets(scope).forEach((el) => {
        if (el._pwrObserved) return;
        el._pwrObserved = true;
        this.observer.observe(el);
      });
    }

    _onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      });
    }

    revealAllInstantly(scope) {
      this._targets(scope).forEach((el) => el.classList.add('is-visible'));
    }
  }

  /* ==========================================================================
     LAZY MEDIA
     Progressive: honours native loading="lazy" but adds a decode-on-approach
     path for [data-pwr-lazy] backgrounds and <video> that native lazy misses.
     ========================================================================== */
  class LazyMedia {
    constructor() {
      this.observer = null;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => this._onIntersect(entries),
          { rootMargin: '200px 0px', threshold: 0.01 }
        );
      }
      utils.ready(() => this.scan(document));
      bus.on(EVENTS.sectionLoad, (d) => this.scan(d.target || document));
    }

    scan(scope) {
      const nodes = utils.qsa('[data-pwr-lazy]', scope || document);
      if (!this.observer) {
        nodes.forEach((el) => this._load(el));
        return;
      }
      nodes.forEach((el) => {
        if (el._pwrLazy) return;
        el._pwrLazy = true;
        this.observer.observe(el);
      });
    }

    _onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this._load(entry.target);
        this.observer.unobserve(entry.target);
      });
    }

    _load(el) {
      const bg = el.getAttribute('data-pwr-lazy');
      if (bg) {
        el.style.backgroundImage = `url("${bg}")`;
        el.classList.add('is-loaded');
      }
      if (el.tagName === 'VIDEO') {
        utils.qsa('source[data-src]', el).forEach((s) => { s.src = s.getAttribute('data-src'); });
        if (el.getAttribute('data-src')) el.src = el.getAttribute('data-src');
        el.load();
        el.classList.add('is-loaded');
      }
    }
  }

  /* ==========================================================================
     PARALLAX
     Sets a --pwr-parallax custom property (-1..1) on [data-pwr-parallax]
     elements, consumed by pwr-animations.css. Uses the central scroll event;
     disabled entirely under reduced motion or on mobile (perf).
     ========================================================================== */
  class Parallax {
    constructor() {
      this.items = [];
      if (reduced()) return;
      utils.ready(() => this.scan(document));
      bus.on(EVENTS.sectionLoad, (d) => this.scan(d.target || document));
      bus.on(EVENTS.scroll, () => this.update());
      bus.on(EVENTS.reducedMotionChange, (m) => { if (m.reducedMotion) this.disable(); });
    }

    scan(scope) {
      if (reduced() || utils.isMobile()) return;
      utils.qsa('[data-pwr-parallax]', scope || document).forEach((el) => {
        if (this.items.includes(el)) return;
        this.items.push(el);
      });
      this.update();
    }

    update() {
      if (reduced() || utils.isMobile() || !this.items.length) return;
      const vh = window.innerHeight;
      this.items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        // -1 (above viewport centre) .. 1 (below), clamped.
        const ratio = utils.clamp((center - vh / 2) / vh, -1, 1);
        el.style.setProperty('--pwr-parallax', ratio.toFixed(3));
      });
    }

    disable() {
      this.items.forEach((el) => el.style.removeProperty('--pwr-parallax'));
      this.items = [];
    }
  }

  /* ==========================================================================
     COUNT-UP  (performance meters / stat numbers)
     Animates [data-pwr-countup] from 0 to its numeric text when revealed.
     Also animates [data-pwr-meter-fill] width. Reduced-motion => jump to end.
     ========================================================================== */
  class CountUp {
    constructor() {
      this.observer = ('IntersectionObserver' in window)
        ? new IntersectionObserver((e) => this._onIntersect(e), { threshold: 0.4 })
        : null;
      utils.ready(() => this.scan(document));
      bus.on(EVENTS.sectionLoad, (d) => this.scan(d.target || document));
    }

    scan(scope) {
      const nodes = utils.qsa('[data-pwr-countup]', scope || document);
      if (!this.observer || reduced()) {
        nodes.forEach((el) => this._settle(el));
        return;
      }
      nodes.forEach((el) => {
        if (el._pwrCount) return;
        el._pwrCount = true;
        this.observer.observe(el);
      });
    }

    _onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this._run(entry.target);
        this.observer.unobserve(entry.target);
      });
    }

    _settle(el) {
      const end = parseFloat(el.getAttribute('data-pwr-countup')) || parseFloat(el.textContent) || 0;
      el.textContent = String(Math.round(end));
    }

    _run(el) {
      const end = parseFloat(el.getAttribute('data-pwr-countup')) || parseFloat(el.textContent) || 0;
      const duration = parseInt(el.getAttribute('data-duration'), 10) || 900;
      const start = performance.now();
      const tick = (now) => {
        const p = utils.clamp((now - start) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        el.textContent = String(Math.round(end * eased));
        if (p < 1) window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    }
  }

  /* ==========================================================================
     RIPPLE  (button micro-interaction, opt-in via .pwr-ripple-host)
     ========================================================================== */
  function initRipple() {
    utils.on(document, 'pointerdown', '.pwr-ripple-host', (event, host) => {
      if (reduced()) return;
      const rect = host.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const span = document.createElement('span');
      span.className = 'pwr-ripple';
      span.style.width = span.style.height = size + 'px';
      span.style.left = (event.clientX - rect.left - size / 2) + 'px';
      span.style.top = (event.clientY - rect.top - size / 2) + 'px';
      host.appendChild(span);
      span.addEventListener('animationend', () => span.remove(), { once: true });
    });
  }

  /* -------------------------------------------------------------- BOOTSTRAP */
  const animation = {
    reveal: new RevealController(),
    lazy: new LazyMedia(),
    parallax: new Parallax(),
    countUp: new CountUp(),
  };
  utils.ready(initRipple);

  window.PWR.animation = animation;
})();
